import React from 'react';
import { DateFormat, MediaPreviewState } from 'stageclip-common';
import moment from 'moment-timezone';
import { faDownload, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import { AppTable, AppTableColumn } from '../../table/appTable';
import Button from '../../buttons/button';
import { ActionButtonsProps } from '../ClipGeneratorPage.types';

import { TemplatesTableProps } from './template.types';
import styles from './templateManagement.module.scss';

type VideoStateProps = {
  value: any;
  className: string;
};

const getPreviewLink = (row: any) =>
  row.videoPreviewUrl ? (
    <a
      href={`${process.env.REACT_APP_PORTALS_PROTOCOL}://${row.videoPreviewUrl}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
    >
      Video Preview
    </a>
  ) : (
    <span>Unavailable</span>
  );

const VideoState = ({ value, className }: VideoStateProps) => <span className={className}>{value}</span>;

const getStatusCell = (row: any) => {
  switch (row.value) {
    case MediaPreviewState.NotAvailable:
      return <VideoState value="Waiting" className={styles.waiting} />;
    case MediaPreviewState.SentToGenerator:
      return <VideoState value="Generating" className={styles.generating} />;
    case MediaPreviewState.Processing:
      return <VideoState value="Processing" className={styles.processing} />;
    case MediaPreviewState.Available:
      return <VideoState value="Available" className={styles.available} />;
    case MediaPreviewState.Error:
      return <VideoState value="Error" className={styles.error} />;
    default:
      return null;
  }
};

const ActionButtons = ({ original, onTemplateDelete, onTemplateDownload, onTemplateUpdate }: ActionButtonsProps) => (
  <>
    <Button
      text="Download Template"
      icon={faDownload}
      slimButton
      onClick={e => {
        e.stopPropagation();
        onTemplateDownload(original.id);
      }}
    />
    <Button
      text="Update Template"
      icon={faEdit}
      slimButton
      onClick={e => {
        e.stopPropagation();
        onTemplateUpdate(original);
      }}
    />
    <Button
      text="Delete Template"
      icon={faTrash}
      slimButton
      onClick={e => {
        e.stopPropagation();
        onTemplateDelete(original.id);
      }}
    />
  </>
);

export const TemplatesTable = ({
  templates,
  onClickDelete,
  onClickDownload,
  onClickUpdate,
  getTemplatesLoading,
}: TemplatesTableProps) => {
  const columns: AppTableColumn[] = [
    {
      Header: 'Preview',
      id: 'preview',
      accessor: 'videoPreviewUrl',
      width: 150,
      Cell: getPreviewLink,
    },
    {
      Header: 'Name',
      id: 'name',
      accessor: 'name',
      width: 400,
    },
    {
      Header: 'Upload Date',
      id: 'uploadDate',
      accessor: 'uploadDate',
      width: 200,
      Cell: ({ original }) => moment(original.uploadDate).format(DateFormat.DateTimeWithTimezone),
    },
    {
      Header: 'Status',
      id: 'status',
      accessor: 'videoPreviewState',
      Cell: getStatusCell,
      width: 100,
    },
    {
      Header: 'Actions',
      id: 'actions',
      width: 600,
      Cell: ({ original }) => (
        <ActionButtons
          original={original}
          onTemplateDelete={onClickDelete}
          onTemplateDownload={onClickDownload}
          onTemplateUpdate={onClickUpdate}
        />
      ),
    },
  ];

  return (
    <AppTable
      loading={getTemplatesLoading}
      columns={columns}
      tableName="template-management"
      data={templates || []}
      defaultSorted={[{ id: 'index', desc: false }]}
    />
  );
};
